import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Search as SearchIcon, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FollowButton } from '@/components/profile/FollowButton';
import { useAuth } from '@/contexts/AuthContext';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';

interface UserProfile {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  bio: string;
  is_verified: boolean;
}

export default function Search() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [suggestedUsers, setSuggestedUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSuggestedUsers();
  }, [user]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const loadSuggestedUsers = async () => {
    if (!user) return;

    // Get users the current user is already following
    const { data: following } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', user.id);

    const followingIds = following?.map(f => f.following_id) || [];

    // Get suggested users (not followed, not self)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, bio, is_verified')
      .neq('id', user.id)
      .not('id', 'in', `(${followingIds.join(',')})`)
      .limit(10);

    if (profiles) {
      setSuggestedUsers(profiles);
    }
  };

  const performSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);

    const query = searchQuery.trim();
    const isUsernameSearch = query.startsWith('@');
    const searchTerm = isUsernameSearch ? query.slice(1) : query;

    let queryBuilder = supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, bio, is_verified');

    if (isUsernameSearch) {
      // Exact username search
      queryBuilder = queryBuilder.ilike('username', `${searchTerm}%`);
    } else {
      // Search in both username and display name
      queryBuilder = queryBuilder.or(
        `username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`
      );
    }

    const { data } = await queryBuilder.limit(20);

    if (data) {
      setSearchResults(data);
    }

    setLoading(false);
  };

  const displayUsers = searchQuery.trim() ? searchResults : suggestedUsers;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <BackButton to="/" />
        <div className="max-w-4xl mx-auto">
        <Card className="p-6 mb-6">
          <h1 className="text-3xl font-bold mb-4">Search Users</h1>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search by username or name... Use @ for exact username"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-lg"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Tip: Use @username for exact username search
          </p>
        </Card>

        <div className="mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">
            {searchQuery.trim() ? 'Search Results' : 'Suggested Users'}
          </h2>
          {searchQuery.trim() && (
            <Badge variant="secondary">{displayUsers.length} found</Badge>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          </div>
        ) : displayUsers.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">
              {searchQuery.trim()
                ? 'No users found matching your search'
                : 'No suggestions available'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displayUsers.map((profile) => (
              <Card
                key={profile.id}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/profile/${profile.username}`)}
              >
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={profile.avatar_url} />
                    <AvatarFallback>
                      {profile.display_name?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg truncate">
                        {profile.display_name || profile.username}
                      </h3>
                      {profile.is_verified && (
                        <Badge variant="secondary" className="text-xs">
                          ✓ Verified
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      @{profile.username}
                    </p>
                    {profile.bio && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {profile.bio}
                      </p>
                    )}
                    {user && profile.id !== user.id && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <FollowButton userId={profile.id} size="sm" />
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
